'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { createClient } from '@/utils/supabase/client';

const STORAGE_PREFIX = 'aifit_tz_sync:';
const RESYNC_AFTER_MS = 12 * 60 * 60 * 1000;
const POLL_MS = 10 * 60 * 1000;

function readDeviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Bogota';
  } catch {
    return 'America/Bogota';
  }
}

function readStored(userId) {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(userId, value) {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${userId}`, JSON.stringify(value));
  } catch {
    // Private mode / quota: syncing again next visit is acceptable.
  }
}

/** Cookie readable by RSC so "today" matches device calendar (not UTC). */
function writeTimeZoneCookie(timeZone) {
  try {
    const safe = encodeURIComponent(String(timeZone || 'America/Bogota').trim() || 'America/Bogota');
    document.cookie = `aifit_tz=${safe}; path=/; max-age=${60 * 60 * 24 * 370}; samesite=lax`;
  } catch {
    // ignore
  }
}

/**
 * Keeps profiles.timezone aligned with wherever the user physically is.
 * Travelers change timezone mid-session, so this re-checks on focus and on a
 * slow poll, not only on first paint, and never assumes a fixed country.
 */
export default function DeviceTimeZoneSync() {
  const router = useRouter();
  const locale = useLocale();
  const supabase = useMemo(() => createClient(), []);
  const inFlightRef = useRef(false);

  const syncTimeZone = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const { data } = await supabase.auth.getSession();
      const userId = data?.session?.user?.id;
      if (!userId) return;

      const timeZone = readDeviceTimeZone();
      writeTimeZoneCookie(timeZone);
      const stored = readStored(userId);
      const isSameZone = stored?.timeZone === timeZone;
      const isFresh = Number(stored?.syncedAt) > Date.now() - RESYNC_AFTER_MS;
      if (isSameZone && isFresh) return;

      const res = await fetch('/api/profile-engine/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timeZone, locale }),
      });
      if (!res.ok) return;

      writeStored(userId, { timeZone, syncedAt: Date.now() });

      // Re-arm today's pushes in the new zone (sleep/meals/gym) without waiting for cron.
      if (!stored?.timeZone || !isSameZone) {
        fetch('/api/notifications/schedule', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, timeZone, locale }),
        }).catch(() => {});
        router.refresh();
      }
    } catch {
      // Non-blocking: the next focus or poll retries.
    } finally {
      inFlightRef.current = false;
    }
  }, [locale, router, supabase]);

  useEffect(() => {
    syncTimeZone();

    const onVisible = () => {
      if (document.visibilityState === 'visible') syncTimeZone();
    };
    const interval = window.setInterval(syncTimeZone, POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', syncTimeZone);

    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') syncTimeZone();
    });

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', syncTimeZone);
      authListener?.subscription?.unsubscribe();
    };
  }, [supabase, syncTimeZone]);

  return null;
}
